import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useMemo } from "react";
import { del, fetchData, patch, post } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import type {
  CommandProject,
  CommandProjectList,
  CommandProjectVisibility,
  CreateCommandProjectPayload,
  UpdateCommandProjectPayload,
} from "@/types/command-agent";
import { toast } from "sonner";

const friendlyErrorDescription = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (typeof data?.detail === "string" && data.detail.trim()) return data.detail;
  if (Array.isArray(data?.detail) && typeof data.detail[0]?.msg === "string") {
    return data.detail[0].msg;
  }
  if (typeof data?.error === "string" && data.error.trim()) return data.error;
  return fallback;
};

type ProjectsInfinite = InfiniteData<CommandProjectList>;

function prependProject(
  data: ProjectsInfinite | undefined,
  project: CommandProject
): ProjectsInfinite {
  if (!data?.pages?.length) {
    return {
      pages: [{ items: [project], item_count: 1, page_count: 1 }],
      pageParams: [1],
    };
  }
  const already = data.pages.some((page) =>
    page.items.some((item) => item.id === project.id)
  );
  if (already) return data;
  const [first, ...rest] = data.pages;
  return {
    ...data,
    pages: [
      {
        ...first,
        items: [project, ...first.items],
        item_count: (first.item_count ?? first.items.length) + 1,
      },
      ...rest,
    ],
  };
}

interface UseCommandProjectsOptions {
  visibility?: CommandProjectVisibility;
  search?: string;
  enabled?: boolean;
}

export function useCommandProjects(options?: UseCommandProjectsOptions) {
  const queryClient = useQueryClient();
  const visibility = options?.visibility;
  const search = options?.search?.trim() || "";
  const enabled = options?.enabled !== false;

  const { hasPermission: canListProjects } = useProtectedQuery({
    queryKey: ["_gated_list_command_projects"],
    queryFn: async () => null,
    requiredPermissions: ["LIST_COMMAND_PROJECTS"],
    enabled: false,
  });

  const {
    data: projectsData,
    isLoading: isLoadingProjects,
    isFetching: isFetchingProjects,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<CommandProjectList>({
    queryKey: ["command-projects", visibility ?? "all", search],
    initialPageParam: 1,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        limit: "50",
        sort_by: "updated_at",
        sort_order: "desc",
      });
      if (visibility) params.set("visibility", visibility);
      if (search) params.set("search", search);
      return fetchData(`/api/command-team/projects?${params.toString()}`);
    },
    getNextPageParam: (lastPage, pages) => {
      if (pages.length < lastPage.page_count) return pages.length + 1;
      return undefined;
    },
    enabled: enabled && canListProjects,
  });

  const allProjects = useMemo(() => {
    if (!projectsData?.pages) return [];
    const raw = projectsData.pages.flatMap((page) => page.items);
    const seen = new Set<string>();
    return raw.filter((project) => {
      if (seen.has(project.id)) return false;
      seen.add(project.id);
      return true;
    });
  }, [projectsData]);

  const invalidateProjects = () => {
    queryClient.invalidateQueries({ queryKey: ["command-projects"] });
  };

  const createProjectMutation = useMutation({
    mutationFn: async (payload: CreateCommandProjectPayload) => {
      const response = await post("/api/command-team/projects", payload);
      return response.data as { id: string };
    },
    onSuccess: async (data, variables) => {
      const now = new Date().toISOString();
      const projectVisibility = variables.visibility ?? "PERSONAL";
      const optimisticProject: CommandProject = {
        id: data.id,
        name: variables.name,
        description: variables.description ?? null,
        color: variables.color ?? null,
        visibility: projectVisibility,
        shared_roles: variables.shared_roles ?? [],
        is_owner: true,
        session_count: 0,
        instructions: variables.instructions ?? null,
        created_at: now,
        updated_at: now,
      };

      // Sidebar lists PERSONAL / TEAM separately — patch those caches immediately.
      queryClient.setQueriesData<ProjectsInfinite>(
        { queryKey: ["command-projects", projectVisibility] },
        (old) => prependProject(old, optimisticProject)
      );
      queryClient.setQueriesData<ProjectsInfinite>(
        { queryKey: ["command-projects", "all"] },
        (old) => prependProject(old, optimisticProject)
      );

      await queryClient.refetchQueries({ queryKey: ["command-projects"] });
      toast.success("Project created", {
        description: "Move chats into it from their menu.",
      });
    },
    onError: (error: unknown) => {
      toast.error("Failed to create project", {
        description: friendlyErrorDescription(error, "Please try again."),
      });
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async ({
      projectId,
      payload,
    }: {
      projectId: string;
      payload: UpdateCommandProjectPayload;
    }) => {
      const response = await patch(`/api/command-team/projects/${projectId}`, payload);
      return response.data as CommandProject;
    },
    onSuccess: (_data, variables) => {
      invalidateProjects();
      queryClient.invalidateQueries({ queryKey: ["command-project", variables.projectId] });
      toast.success("Project updated");
    },
    onError: (error: unknown) => {
      toast.error("Failed to update project", {
        description: friendlyErrorDescription(error, "Please try again."),
      });
    },
  });

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const response = await del(`/api/command-team/projects/${projectId}`);
      return response.data as { deleted: string };
    },
    onSuccess: (_data, projectId) => {
      invalidateProjects();
      queryClient.invalidateQueries({ queryKey: ["command-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["command-project-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["command-project-sessions-page"] });
      toast.success("Project deleted", {
        description: "Chats from that project moved to Other chats.",
      });
      return projectId;
    },
    onError: (error: unknown) => {
      toast.error("Failed to delete project", {
        description: friendlyErrorDescription(error, "Please try again."),
      });
    },
  });

  return {
    allProjects,
    isLoadingProjects,
    isFetchingProjects,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    canListProjects,
    createProject: createProjectMutation.mutate,
    createProjectAsync: createProjectMutation.mutateAsync,
    isCreatingProject: createProjectMutation.isPending,
    updateProject: updateProjectMutation.mutate,
    isUpdatingProject: updateProjectMutation.isPending,
    deleteProject: deleteProjectMutation.mutate,
    isDeletingProject: deleteProjectMutation.isPending,
  };
}

export function useCommandProjectDetail(projectId: string | null) {
  const { hasPermission: canViewProject } = useProtectedQuery({
    queryKey: ["_gated_view_command_project"],
    queryFn: async () => null,
    requiredPermissions: ["VIEW_COMMAND_PROJECT"],
    enabled: false,
  });

  return useQuery<CommandProject>({
    queryKey: ["command-project", projectId],
    queryFn: () => fetchData(`/api/command-team/projects/${projectId}`),
    enabled: canViewProject && Boolean(projectId),
    staleTime: 0,
    gcTime: 0,
  });
}
